import { AxiosError } from 'axios';
import { getFullUrl } from './url';

export interface BaseResponseErrorData {
    status: number;
    title: string;
    traceId: string;
    type: string;
}

export class ApiBaseError<E extends BaseResponseErrorData = BaseResponseErrorData> {
    constructor(protected axiosError: AxiosError<E>) {}

    getTitle() {
        return this.axiosError.response?.data?.title ?? 'API Request Error';
    }

    getMessage(): string | undefined {
        return `Status Code: ${this.axiosError.response?.status}`;
    }
}

export interface BadRequestErrorData extends BaseResponseErrorData {
    errors: { [key: string]: string[] };
}

export class BadRequestError extends ApiBaseError<BadRequestErrorData> {
    getMessage() {
        if (this.axiosError.response?.data.errors) {
            return Object.entries(this.axiosError.response.data.errors)
                .map(([key, v]) => {
                    return `${key}: ${v.join(',')}`;
                })
                .join('. ');
        }
    }
}

export interface UnAuthorizedRequestErrorData extends BaseResponseErrorData {
    errors: { errorMessage: string[] };
}

export class UnAuthorizedRequestError extends ApiBaseError<UnAuthorizedRequestErrorData> {
    getMessage() {
        return 'You are not authorized to do this action.';
    }
}

export interface ForbiddenRequestErrorData extends BaseResponseErrorData {
    errors: { errorMessage: string[] };
}

export class ForbiddenRequestError extends ApiBaseError<ForbiddenRequestErrorData> {
    getMessage() {
        return 'You have no permission to do this action.';
    }
}

export interface NotFoundRequestErrorData extends BaseResponseErrorData {
    errors: { errorMessage: string[] };
}

export class NotFoundRequestError extends ApiBaseError<NotFoundRequestErrorData> {
    getMessage() {
        return 'Resource not found.';
    }
}

export interface ConflictRequestErrorData extends BaseResponseErrorData {
    errors: { errorMessage: string[] };
}

export class ConflictRequestError extends ApiBaseError<ConflictRequestErrorData> {
    getMessage() {
        if (this.axiosError.response?.data.errors.errorMessage) {
            return this.axiosError.response?.data.errors.errorMessage.join('. ');
        }
    }
}

export interface ServerErrorData extends BaseResponseErrorData {
    errors: { errorMessage: string[] };
}

export class ServerRequestError extends ApiBaseError<ServerErrorData> {
    getMessage() {
        if (this.axiosError.response?.status === 0) {
            const fullUrl = getFullUrl(
                this.axiosError.config.params,
                this.axiosError.config.baseURL,
                this.axiosError.config.url
            );
            return `Unable to connect to ${fullUrl}. Please try again.`;
        }
        if (this.axiosError.response?.data.errors.errorMessage) {
            const errorMessage = `${this.axiosError.response?.data.errors.errorMessage.join(
                '. '
            )}. TraceId: ${this.axiosError.response?.data.traceId}`;
            return errorMessage;
        }
    }
}

export function isServerRequestError(error: unknown): error is AxiosError<ServerErrorData> {
    return (
        error instanceof AxiosError &&
        !!error.response &&
        (error.response.status >= 500 || error.response.status === 0)
    );
}

export function tryGetServerError(error: unknown): ApiBaseError | null {
    if (isServerRequestError(error)) {
        return new ServerRequestError(error);
    }
    return null;
}

export function tryGetClientError(error: unknown): ApiBaseError | null {
    if (
        error instanceof AxiosError &&
        !!error.response &&
        error.response.status >= 400 &&
        error.response.status < 500
    ) {
        return apiErrorFactory(error);
    }
    return null;
}

export function isBadRequestError(error: unknown): error is AxiosError<BadRequestErrorData> {
    return error instanceof AxiosError && !!error.response && error.response.status === 400;
}

export function isUnAuthorizedRequestError(
    error: unknown
): error is AxiosError<UnAuthorizedRequestErrorData> {
    return error instanceof AxiosError && !!error.response && error.response.status === 401;
}

export function isForbiddenRequestError(
    error: unknown
): error is AxiosError<ForbiddenRequestErrorData> {
    return error instanceof AxiosError && !!error.response && error.response.status === 403;
}

export function isNotFoundRequestError(
    error: unknown
): error is AxiosError<NotFoundRequestErrorData> {
    return error instanceof AxiosError && !!error.response && error.response.status === 404;
}

export function isConflictRequestError(
    error: unknown
): error is AxiosError<ConflictRequestErrorData> {
    return error instanceof AxiosError && !!error.response && error.response.status === 409;
}

export function apiErrorFactory(
    axiosError: AxiosError<BaseResponseErrorData>
): ApiBaseError | null {
    if (isBadRequestError(axiosError)) {
        return new BadRequestError(axiosError);
    }
    if (isForbiddenRequestError(axiosError)) {
        return new ForbiddenRequestError(axiosError);
    }
    if (isNotFoundRequestError(axiosError)) {
        return new NotFoundRequestError(axiosError);
    }
    if (isConflictRequestError(axiosError)) {
        return new ConflictRequestError(axiosError);
    }
    if (isServerRequestError(axiosError)) {
        return new ServerRequestError(axiosError);
    }
    return null;
}
